// src/components/DesktopGate.tsx
//
// Full-viewport gate that blocks desktop/tablet visitors and asks
// them to return on their phone — the Instagram approach. Mobile
// is the only experience we currently optimize for; rendering the
// full app on a 1440px desktop reveals layout assumptions we
// haven't paid for yet.
//
// Activation rules:
//   - Viewport is at MUI breakpoint `md` or wider (≥900px). Below
//     that, behaves as a transparent pass-through.
//   - The user's Firestore profile does NOT have `allowDesktop: true`
//     at its root. That field is intentionally not exposed in any
//     UI — it is set manually in the Firestore console for staff /
//     QA who need desktop access. Default (missing/false) is gated.
//   - The current path is not in `bypassPaths`. Legal pages
//     (/privacy, /terms) bypass — they must remain reachable from
//     any device, the same reason the i18n extractor excludes them
//     from translation.
//
// Note: the breakpoint check uses `useMediaQuery`, which subscribes
// to viewport changes — resizing the window or rotating an iPad
// will re-evaluate live. Matches the convention used in AppShell
// and CookieBanner.

import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Link as MuiLink,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Logo } from './Logo';
import { useT } from '../i18n/useT';

interface Props {
  /** Whether the current user's Firestore profile has the
   *  `allowDesktop: true` override set. When true, this component
   *  is a transparent pass-through regardless of viewport. */
  allowDesktop: boolean;
  /** Path prefixes that always pass through — legal pages, etc.
   *  Matched as exact strings or path-prefix segments. */
  bypassPaths?: readonly string[];
  children: React.ReactNode;
}

const DEFAULT_BYPASS_PATHS = ['/privacy', '/terms'] as const;

export const DesktopGate: React.FC<Props> = ({
  allowDesktop,
  bypassPaths = DEFAULT_BYPASS_PATHS,
  children,
}) => {
  const theme = useTheme();
  // `down('md')` is true for viewports BELOW 900px (the mobile
  // range we support). We invert to detect "needs gating".
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktopViewport = !isMobileViewport;

  const path =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const isBypassPath = bypassPaths.some(
    (p) => path === p || path.startsWith(p + '/')
  );

  if (!isDesktopViewport || allowDesktop || isBypassPath) {
    return <>{children}</>;
  }

  return <DesktopGateScreen />;
};

const DesktopGateScreen: React.FC = () => {
  const t = useT();
  return (
    <Box
      sx={{
        // 100dvh handles iPad's URL bar correctly when this gate is
        // ever shown there; the @supports fallback covers older
        // browsers that don't know about dvh.
        height: '100dvh',
        '@supports not (height: 100dvh)': { height: '100vh' },
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Match App.tsx splash gradient direction so the gate feels
        // like a continuation of the brand rather than an error page.
        bgcolor: 'background.default',
        px: 4,
        textAlign: 'center',
      }}
    >
      <Stack
        spacing={3}
        alignItems="center"
        sx={{
          maxWidth: 480,
          // Subtle entrance — the same pattern FullPageLoader uses,
          // for consistency with the rest of the app's "first
          // paint" feel.
          animation: 'gateFadeIn 350ms ease-out',
          '@keyframes gateFadeIn': {
            '0%': { opacity: 0, transform: 'translateY(6px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Logo size={72} alt={t('Instant Tours')} />

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}
        >
          {t('Instant Tours is built for mobile')}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.55 }}
        >
          {t(
            'Open this page on your phone to use the app. The mobile experience is where Instant Tours feels its best — a desktop version is coming later.'
          )}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ pt: 1 }}
        >
          {t('Tip: scan a QR code from your phone to open this URL there.')}
        </Typography>

        {/* Legal links stay reachable from the gate — these are the
            same paths DesktopGate's bypassPaths lets through, so
            tapping them opens normally. */}
        <Stack
          direction="row"
          spacing={3}
          sx={{ pt: 2, fontSize: '0.85rem' }}
        >
          <MuiLink
            href="/privacy"
            color="text.secondary"
            underline="hover"
          >
            {t('Privacy')}
          </MuiLink>
          <MuiLink
            href="/terms"
            color="text.secondary"
            underline="hover"
          >
            {t('Terms')}
          </MuiLink>
        </Stack>
      </Stack>
    </Box>
  );
};
